import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileService } from '../service/Profile.service';
import { Order } from '../dto/Profile.dto';
import { ApiService } from '../../../shared/service/Apiservice';
import axios from 'axios';
import { useCart } from '../../../contexts/CartContext';
import "./styles/ViewOrder.css";

interface ViewOrderProps {
  onViewOrder: (orderId: string) => void;
}

const ViewOrder: React.FC<ViewOrderProps> = ({ onViewOrder }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create API service instances
  const apiService = React.useMemo(() => new ApiService(axios.create()), []);
  const profileService = React.useMemo(() => new ProfileService(apiService), [apiService]);

  // Fetch orders from API
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      const response = await profileService.GetUserOrder();
      
      if (response.success && response.orders) {
        const transformedOrders = response.orders.map((order: any) => ({
          ...order,
          items: order.items.map((item: any) => ({
            ...item,
            ...(item.product || {}),
            product: item.product || { _id: '', title: 'Unknown Product', imageUrl: '' }
          }))
        }));
        
        setOrders(transformedOrders);
      } else {
        setError('Failed to load orders');
      }
    } catch (error) {
      setError('Failed to load orders. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Helper functions from reference
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric'
    };
    try {
      return new Date(dateString).toLocaleDateString('en-US', options);
    } catch (e) {
      return 'Invalid date';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      case 'pending':
      case 'paid':
        return 'Order Placed';
      case 'processing':
        return 'Processing';
      case 'shipped':
        return 'Shipped';
      default:
        return 'Order Placed';
    }
  };

  const handleTrackNow = (orderId: string) => {
    navigate(`/track-order/${orderId}`);
  };

  const handleViewMore = (orderId: string) => {
    onViewOrder(orderId);
  };

  const handleReorder = async (order: Order) => {
    try {
      // Add all items from the order to cart
      for (const item of order.items) {
        const product = {
          id: item.product._id,
          _id: item.product._id,
          title: item.product.title,
          price: item.price,
          imageUrl: item.variantImage || item.product.imageUrl,
        };

        await addToCart(product, item.quantity, item.variantSku);
      }

      navigate('/cart');
    } catch (error) {
    }
  };

  if (isLoading) {
    return (
      <div className="view-order-container">
        <div className="main-card">
          <h2 className="your-order-title">Your Order</h2>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            Loading your orders...
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-order-container">
        <div className="main-card">
          <h2 className="your-order-title">Your Order</h2>
          <div style={{ textAlign: 'center', padding: '2rem', color: '#dc3545' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="view-order-container">
      <div className="main-card">
        <h2 className="your-order-title">Your Order</h2>

        {orders.length > 0 ? orders.map((order, idx) => (
          <div className="sub-card" key={idx}>
            <div className="sub-card-header">
              <div className="order-id-and-status">
                <span className="order-id">Order Id  {order.orderId || order._id}</span>
                <span className="status-badge">{getStatusText(order.status)}</span>
              </div>
              <div className="order-price">₹{typeof order.totalAmount === 'number' ? Math.round(order.totalAmount) : '0'}</div>
            </div>

            <div className="placed-on">Placed on {formatDate(order.createdAt)}</div>

            <div className="thumbs-and-actions">
              <div className="thumbs">
                {order.items && order.items.length > 0 ? order.items.slice(0, 4).map((item, i) => {
                  const displayImage = item.variantImage || (typeof item.product === 'object' ? item.product.imageUrl : '');
                  const displayTitle = item.variantTitle || (typeof item.product === 'object' ? item.product.title : 'Product');
                  
                  return (
                    <img
                      key={i}
                      src={displayImage || "/images/dummy.png"}
                      alt={`ordered item ${displayTitle}`}
                      className="thumb"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "/images/dummy.png";
                      }}
                    />
                  );
                }) : [0, 1, 2, 3].map((i) => (
                  <img
                    key={i}
                    src="/images/dummy.png"
                    alt={`ordered item ${i + 1}`}
                    className="thumb"
                  />
                ))}
              </div>

              <div className="actions">
                {order.status.toLowerCase() === 'delivered' ? (
                  <>
                    <button 
                      className="track-btn"
                      onClick={() => handleReorder(order)}
                    >
                      Re-order
                    </button>
                    <button 
                      className="view-btn"
                      onClick={() => handleViewMore(order._id)}
                    >
                      View More
                    </button>
                  </>
                ) : order.status.toLowerCase() === 'cancelled' ? (
                  <button 
                    className="view-btn"
                    onClick={() => handleViewMore(order._id)}
                  >
                    Order Detail
                  </button>
                ) : (
                  <button 
                    className="track-btn"
                    onClick={() => handleTrackNow(order._id)}
                  >
                    Track Now
                  </button>
                )}
              </div>
            </div>
          </div>
        )) : (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            <p>No orders found. Start shopping to see your orders here!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewOrder;


